import {
  BacktestingLineChartType,
  BacktestingRunType,
  PeriodicStatsDataPoint,
  RawLineChartDataPoint,
} from "@shared/types";
import { useCallback, useMemo } from "react";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import { formatTimeSeriesChartData } from "@shared/utils/charts";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { convertToObjectsArray } from "@shared/utils/arrays";
import { toFixed } from "@shared/utils/numbers";

interface usePeriodicStatsProps {
  backtestingRun?: BacktestingRunType;
  periodicStatsData?: { headers: string[]; data: any };
  periodicStats?: PeriodicStatsDataPoint[];
  isGraphMinified?: boolean;
  numPoints?: number;
  suffix?: string;
  includeInitialFunds?: boolean;
}

const usePeriodicStats = ({
  backtestingRun,
  periodicStatsData,
  periodicStats: propsPeriodicStats,
  isGraphMinified,
  numPoints,
  suffix,
  includeInitialFunds,
}: usePeriodicStatsProps) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const displayCurrencyPrecision = getDisplayCurrencyAmountPrecision();
  const usdPrecision = getDisplayCurrencyAmountPrecision(
    DISPLAY_CURRENCIES.USD
  );
  const btcPrecision = getDisplayCurrencyAmountPrecision(
    DISPLAY_CURRENCIES.BTC
  );

  const periodicStats: PeriodicStatsDataPoint[] | undefined = useMemo(
    () =>
      periodicStatsData
        ? convertToObjectsArray(
            periodicStatsData?.data,
            periodicStatsData?.headers
          )
        : undefined,
    [periodicStatsData]
  );
  const periodicStatsToUse = periodicStats ?? propsPeriodicStats;

  const getParsedPeriodicStats = useCallback(
    (lineChartType: BacktestingLineChartType) => {
      return periodicStatsToUse?.map((dataPoint) => {
        const usdValue =
          lineChartType === BacktestingLineChartType.NET_PNL
            ? dataPoint.usd_realised_pnl + dataPoint.usd_unrealised_pnl
            : dataPoint.usd_realised_pnl;
        const btcValue =
          lineChartType === BacktestingLineChartType.NET_PNL
            ? dataPoint.btc_realised_pnl + dataPoint.btc_unrealised_pnl
            : dataPoint.btc_realised_pnl;

        return [dataPoint.correct_to, usdValue, btcValue];
      });
    },
    [periodicStatsToUse]
  );

  const getDisplayValue = (value: number, precision: number) => {
    return value > 999 ? Math.round(value) : toFixed(value, precision);
  };

  const getDisplayedPeriodicStats = useCallback(
    (lineChartType: BacktestingLineChartType) => {
      const timeframe = backtestingRun?.backtesting_time_range;
      const parsedChartData = getParsedPeriodicStats(
        lineChartType
      ) as RawLineChartDataPoint[];
      if (!timeframe) return parsedChartData;
      let dataPoints: RawLineChartDataPoint[] = [];
      const interval =
        (timeframe?.end - timeframe?.start) /
        (numPoints ?? NUM_DISPLAYED_POINTS);

      parsedChartData?.forEach((dataPoint) => {
        const diff = dataPoint?.[0] - timeframe?.start;
        const i = Math.floor(diff / interval);
        if (dataPoint?.[0] > (dataPoints?.[i]?.[0] || 0)) {
          dataPoints[i] = dataPoint;
        }
      });
      const initialFundsData = backtestingRun?.initial_portfolio_size;
      const usdInitialFunds = getDisplayValue(
        initialFundsData?.USD || 0,
        usdPrecision
      );
      const btcInitialFunds = getDisplayValue(
        initialFundsData?.BTC || 0,
        btcPrecision
      );
      if (includeInitialFunds) {
        dataPoints.forEach((dataPoint) => {
          dataPoint[1] = dataPoint[1] + usdInitialFunds;
          dataPoint[2] = (dataPoint[2] || 0) + btcInitialFunds;
        });
      }
      return dataPoints;
    },
    [backtestingRun, getParsedPeriodicStats, includeInitialFunds]
  );

  const getFormattedLineChartData = useCallback(
    (lineChartType: BacktestingLineChartType) => {
      const yValueIndex = displayCurrency === DISPLAY_CURRENCIES.BTC ? 2 : 1;
      return formatTimeSeriesChartData(
        getDisplayedPeriodicStats(lineChartType),
        yValueIndex,
        false,
        suffix,
        displayCurrencyPrecision,
        isGraphMinified
      );
    },
    [displayCurrency, displayCurrencyPrecision, getDisplayedPeriodicStats]
  );

  const realizedPnlLineChart = useMemo(
    () => getFormattedLineChartData(BacktestingLineChartType.REALIZED_PNL),
    [getFormattedLineChartData]
  );

  const netPnlLineChart = useMemo(
    () => getFormattedLineChartData(BacktestingLineChartType.NET_PNL),
    [getFormattedLineChartData]
  );

  return { realizedPnlLineChart, netPnlLineChart };
};

export default usePeriodicStats;

const NUM_DISPLAYED_POINTS = 118;
